import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, CheckCircle2 } from "lucide-react";

const STYLE_OPTS = [
  { v: "rhinestones", l: "Lots of rhinestones / bling" },
  { v: "images", l: "More images / graphics" },
  { v: "wording", l: "More wording / quotes" },
  { v: "mix", l: "A mix of everything" },
];
const LAYOUT_OPTS = [
  { v: "one_main", l: "One main large design" },
  { v: "collage", l: "Collage style with multiple smaller images" },
];
const RHINESTONE_OPTS = [
  { v: "yes", l: "Yes" },
  { v: "no", l: "No" },
  { v: "a_little", l: "A little sparkle" },
  { v: "full_bling", l: "Full bling" },
];

const FIELD_LABEL = "text-[10px] uppercase tracking-[0.25em] text-gold font-bold";

export default function JacketQuestionnaire() {
  const [form, setForm] = useState({
    parent_name: "", parent_email: "",
    child_name: "", child_age: "",
    favourite_colours: "", hobbies: "", characters: "",
    style_preference: [], layout_preference: [],
    favourite_quotes: "", names_initials: "",
    rhinestones: "", jacket_colour: "",
    must_include: "", must_avoid: "",
    inspiration_urls: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleArr = (k, v) => () => {
    const arr = form[k];
    setForm({ ...form, [k]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.parent_email) delete payload.parent_email;
      if (!payload.rhinestones) payload.rhinestones = "none";
      await api.post("/jacket-questionnaire", payload);
      setDone(true);
      toast.success("Brief sent — designing soon.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[60vh] container-x section-y flex items-center bg-ink" data-testid="questionnaire-success">
        <div className="max-w-xl mx-auto card-brutal text-center w-full">
          <CheckCircle2 size={42} className="mx-auto text-rose" />
          <p className="mt-5 font-display text-5xl uppercase text-white">Brief received</p>
          <p className="mt-3 text-white/75">
            I'll start sketching ideas based on what you've shared and reach out before
            your workshop with a moodboard.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="jacket-questionnaire-page" className="bg-ink">
      <div className="container-x section-y">
        <div className="max-w-3xl mx-auto">
          <p className="overline">Step 2 — design brief</p>
          <h1 className="mt-4 font-display text-6xl md:text-7xl text-white">
            Personalised<br /><span className="text-rose">jacket brief.</span>
          </h1>
          <p className="mt-6 text-white/75 text-lg">
            For everyone — adults, teens and children. The more you share, the more
            personal the piece. Fill in as much or as little as you'd like, and Miss
            Lolly will turn it into a moodboard before your session.
          </p>

          <form onSubmit={submit} data-testid="jacket-form" className="mt-12 space-y-8">
            {/* Basics */}
            <section className="card-brutal space-y-5">
              <div className="flex items-center gap-2"><Sparkles size={16} className="text-rose" /><h2 className="font-display text-3xl text-white">The basics</h2></div>
              <div className="grid sm:grid-cols-2 gap-5">
                <label><span className={FIELD_LABEL}>Booking contact name (optional)</span>
                  <input value={form.parent_name} onChange={upd("parent_name")} className="input-brutal mt-2" placeholder="Parent / guardian / yourself" />
                </label>
                <label><span className={FIELD_LABEL}>Booking email (optional)</span>
                  <input type="email" value={form.parent_email} onChange={upd("parent_email")} className="input-brutal mt-2" />
                </label>
                <label><span className={FIELD_LABEL}>Attendee name *</span>
                  <input data-testid="jq-child-name" required value={form.child_name} onChange={upd("child_name")} className="input-brutal mt-2" placeholder="Who's the jacket for?" />
                </label>
                <label><span className={FIELD_LABEL}>Age *</span>
                  <input data-testid="jq-child-age" required value={form.child_age} onChange={upd("child_age")} className="input-brutal mt-2" placeholder="e.g. 9 or adult" />
                </label>
              </div>
            </section>

            {/* Inspiration */}
            <section className="card-brutal space-y-5">
              <h2 className="font-display text-3xl text-white">Inspiration</h2>
              <label className="block"><span className={FIELD_LABEL}>Favourite colours</span>
                <input data-testid="jq-colours" value={form.favourite_colours} onChange={upd("favourite_colours")} className="input-brutal mt-2" placeholder="e.g. hot pink, baby blue, silver" />
              </label>
              <label className="block"><span className={FIELD_LABEL}>Favourite hobbies / interests</span>
                <input data-testid="jq-hobbies" value={form.hobbies} onChange={upd("hobbies")} className="input-brutal mt-2" placeholder="e.g. gymnastics, football, dance, gaming, makeup, music, animals" />
              </label>
              <label className="block"><span className={FIELD_LABEL}>Favourite characters / brands / themes</span>
                <input data-testid="jq-characters" value={form.characters} onChange={upd("characters")} className="input-brutal mt-2" placeholder="e.g. Stitch, Nike, Barbie, Roblox, Marvel" />
              </label>
            </section>

            {/* Style */}
            <section className="card-brutal space-y-6">
              <h2 className="font-display text-3xl text-white">Style</h2>

              <div>
                <p className={`${FIELD_LABEL} mb-3`}>Style — tick any</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {STYLE_OPTS.map((o) => (
                    <label key={o.v} data-testid={`jq-style-${o.v}`}
                      className={`cursor-pointer border-2 px-4 py-3 text-sm transition flex items-start gap-3 ${form.style_preference.includes(o.v) ? "border-rose bg-rose text-white" : "border-white/30 text-white/85 hover:border-rose"}`}>
                      <input type="checkbox" className="mt-1 accent-[#FF2A70]" checked={form.style_preference.includes(o.v)} onChange={toggleArr("style_preference", o.v)} />
                      {o.l}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <p className={`${FIELD_LABEL} mb-3`}>Layout</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {LAYOUT_OPTS.map((o) => (
                    <label key={o.v} data-testid={`jq-layout-${o.v}`}
                      className={`cursor-pointer border-2 px-4 py-3 text-sm transition flex items-start gap-3 ${form.layout_preference.includes(o.v) ? "border-gold bg-gold text-ink" : "border-white/30 text-white/85 hover:border-gold"}`}>
                      <input type="checkbox" className="mt-1 accent-[#D4AF37]" checked={form.layout_preference.includes(o.v)} onChange={toggleArr("layout_preference", o.v)} />
                      {o.l}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <p className={`${FIELD_LABEL} mb-3`}>Sparkle / rhinestones</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {RHINESTONE_OPTS.map((o) => (
                    <label key={o.v} data-testid={`jq-rhinestones-${o.v}`}
                      className={`cursor-pointer border-2 px-4 py-2.5 text-sm text-center transition uppercase tracking-wider ${form.rhinestones === o.v ? "border-lime bg-lime text-ink" : "border-white/30 text-white/85 hover:border-lime"}`}>
                      <input type="radio" name="rhinestones" className="hidden" checked={form.rhinestones === o.v} onChange={() => setForm({ ...form, rhinestones: o.v })} />
                      {o.l}
                    </label>
                  ))}
                </div>
              </div>
            </section>

            {/* Words */}
            <section className="card-brutal space-y-5">
              <h2 className="font-display text-3xl text-white">Words & details</h2>
              <label className="block"><span className={FIELD_LABEL}>Favourite words, quotes or phrases</span>
                <input data-testid="jq-quotes" value={form.favourite_quotes} onChange={upd("favourite_quotes")} className="input-brutal mt-2" placeholder='e.g. "Be You", "Gym Girl", "Born to Shine"' />
              </label>
              <label className="block"><span className={FIELD_LABEL}>Any names, initials or numbers</span>
                <input data-testid="jq-names" value={form.names_initials} onChange={upd("names_initials")} className="input-brutal mt-2" />
              </label>
              <label className="block"><span className={FIELD_LABEL}>Preferred jacket colours / style</span>
                <input data-testid="jq-jacket-colour" value={form.jacket_colour} onChange={upd("jacket_colour")} className="input-brutal mt-2" placeholder="e.g. light denim, black denim, pink jacket" />
              </label>
            </section>

            {/* Wishlist */}
            <section className="card-brutal space-y-5">
              <h2 className="font-display text-3xl text-white">Wishlist & no-gos</h2>
              <label className="block"><span className={FIELD_LABEL}>Anything you definitely DO want included</span>
                <textarea data-testid="jq-must-include" rows={3} value={form.must_include} onChange={upd("must_include")} className="input-brutal mt-2 resize-none" />
              </label>
              <label className="block"><span className={FIELD_LABEL}>Anything you DO NOT want included</span>
                <textarea data-testid="jq-must-avoid" rows={3} value={form.must_avoid} onChange={upd("must_avoid")} className="input-brutal mt-2 resize-none" />
              </label>
              <label className="block"><span className={FIELD_LABEL}>Inspiration picture URLs (optional)</span>
                <textarea data-testid="jq-urls" rows={3} value={form.inspiration_urls} onChange={upd("inspiration_urls")} className="input-brutal mt-2 resize-none" placeholder="Paste Pinterest or Instagram links — one per line" />
              </label>
              <label className="block"><span className={FIELD_LABEL}>Anything else for Miss Lolly?</span>
                <textarea data-testid="jq-notes" rows={3} value={form.notes} onChange={upd("notes")} className="input-brutal mt-2 resize-none" />
              </label>
            </section>

            <div className="flex justify-end">
              <button data-testid="jq-submit" disabled={busy} className="btn-accent disabled:opacity-50">
                {busy ? "Sending…" : "Send my jacket brief"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
